/**
 * The wait between one verification code going out and the next one being allowed.
 *
 * The code step used to arrive with both Resend buttons already live, so the first thing a user did
 * while waiting for an SMS was press Resend — before the first message had had time to land. Every
 * press spends from a sending budget, so the presses that buy nothing are the expensive ones.
 *
 * Deliberately a deadline in wall-clock time rather than a counter ticked down by an interval: a
 * background tab has its timers throttled to once a second at best, so a counter drifts long while a
 * deadline just reads late and self-corrects.
 */
export const RESEND_COOLDOWN_SECONDS = 30;

/**
 * What the SERVER enforces, which is the floor this client must never undercut.
 *
 * The send endpoint answers 429 until this many seconds have passed since the caller's last code. Our
 * own wait is longer on purpose: a client that waits exactly as long as the server is one clock skew
 * away from asking a second too early every time, and the user pays for that with a refusal instead of
 * a code. Used only as the fallback when a 429 arrives with no `Retry-After` to read.
 */
export const SERVER_THROTTLE_SECONDS = 25;

/**
 * The wait a 429 that named NO `Retry-After` puts the user in.
 *
 * Deliberately the client's own cooldown rather than the server's window — and a `Math.max` rather than
 * a third literal, so the relationship holds if either constant is retuned.
 *
 * Waiting exactly as long as the server is the mistake `SERVER_THROTTLE_SECONDS` warns about, and on
 * this path it costs more than one stray refusal: a bare 429 is also what counts toward the shared-budget
 * wall, so the second one — the one a too-short wait goes and fetches — is read as the permanent dead
 * end. The user would wait out our countdown precisely in order to be told the service is unavailable.
 *
 * A 429 that DOES name its wait is obeyed as named; this is only what there is to use when nothing was
 * said.
 */
export const BARE_REFUSAL_WAIT_SECONDS = Math.max(RESEND_COOLDOWN_SECONDS, SERVER_THROTTLE_SECONDS);

/** The longest wait a server header may put us in. A longer one is CLAMPED to this, never discarded — see `parseRetryAfter`. */
const MAX_RETRY_AFTER_SECONDS = 600;

/** When a cooldown started at `now` runs out. */
export function cooldownDeadline(now: number, seconds: number = RESEND_COOLDOWN_SECONDS): number {
  return now + seconds * 1000;
}

/**
 * Whole seconds still to wait — what the countdown shows, and what gates the buttons.
 *
 * Rounded UP, so the last fraction of a second still reads as "1s" rather than as an already-expired
 * "0s" the button does not honour. 0 means the wait is over; a deadline of 0 is "no cooldown running".
 */
export function secondsUntilResend(deadline: number, now: number): number {
  if (!deadline) return 0;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/**
 * How long a 429 says to wait, read from its `Retry-After` header.
 *
 * This is the one number that comes from the side actually doing the throttling, so when it is there it
 * wins over every constant here — including a window the server later retunes away from 25s, which we
 * would otherwise keep guessing wrong.
 *
 * Both forms the spec allows are accepted: delta-seconds (`25`) and an HTTP-date. Only a header that is
 * absent, empty or unparseable answers `undefined` — and then the caller falls back to
 * `BARE_REFUSAL_WAIT_SECONDS`.
 *
 * Everything that DID parse is CLAMPED into [1, 10 minutes] rather than discarded, and the difference
 * matters beyond the number: `undefined` does not merely lose the duration, it reclassifies the refusal
 * as a BARE 429 — the one the caller counts toward the permanent shared-budget wall.
 *
 * - Too long ("come back in 15 minutes") used to answer `undefined`, so it got a 30-second wait and then,
 *   on the next press, a dead end telling the user the service is unavailable.
 * - `Retry-After: 0` is legal delta-seconds and means "retry now". It used to answer `undefined` too, so
 *   a server politely saying "go ahead" was counted as a bare refusal — the same wrong dead end, reached
 *   from the opposite direction. It is floored at 1 rather than returned as 0 because a 0 would render
 *   "you can ask again in 0s", which is not a sentence, and one second after a 429 is not a wait anyone
 *   notices.
 *
 * The ten-minute cap still exists so one hostile or wrong header cannot disable the button for the rest
 * of the session; it just no longer throws the server's answer away to do it.
 */
export function parseRetryAfter(value: string | null | undefined, now: number): number | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Delta-seconds. Tested before the date, because `Date.parse('25')` is a date in some engines.
  const seconds = /^\d+$/.test(trimmed) ? Number(trimmed) : secondsUntilDate(trimmed, now);
  // NaN only. An all-digit header too large for a JS number becomes `Infinity`, and that is still the
  // server naming a wait — a very long one — so it must reach the clamp rather than be discarded as
  // unreadable. `Math.ceil`/`Math.max` pass Infinity through; `Math.min` is what caps it. A date can only
  // ever produce a finite value or `undefined`, so this branch is about delta-seconds alone.
  if (seconds === undefined || Number.isNaN(seconds)) return undefined;
  const whole = Math.ceil(seconds);
  return Math.min(Math.max(whole, 1), MAX_RETRY_AFTER_SECONDS);
}

function secondsUntilDate(value: string, now: number): number | undefined {
  const at = Date.parse(value);
  return Number.isNaN(at) ? undefined : (at - now) / 1000;
}
